// Phonics Scope & Sequence mapping
const SCOPE_SEQUENCE = {
  'Letter Names': {
    grade: 'Kindergarten',
    pattern: 'Letter Recognition',
    examples: 'A, B, C...'
  },
  'Letter Sounds': {
    grade: 'Kindergarten',
    pattern: 'Letter-Sound Correspondence',
    examples: '/a/, /b/, /c/...'
  },
  'VC & CVC': {
    grade: 'Kindergarten',
    pattern: 'CVC Words',
    examples: 'cat, dog, sun'
  },
  'CVCC & CCVC': {
    grade: 'Grade 1',
    pattern: 'Consonant Blends',
    examples: 'stop, clap, frog'
  },
  'CVCe': {
    grade: 'Grade 1',
    pattern: 'Silent E',
    examples: 'cake, bike, home'
  },
  'R-Controlled Vowels': {
    grade: 'Grade 2',
    pattern: 'R-Controlled Syllables',
    examples: 'car, her, bird'
  },
  'Consonant Digraphs': {
    grade: 'Grade 1',
    pattern: 'Digraphs',
    examples: 'shop, chat, think'
  },
  'Vowel Digraphs & Diphthongs': {
    grade: 'Grade 2',
    pattern: 'Vowel Teams',
    examples: 'rain, boat, coin'
  },
  'Vowel Digraphs': {
    grade: 'Grade 2',
    pattern: 'Vowel Teams',
    examples: 'rain, boat, coin'
  },
  'Prefixes & Suffixes': {
    grade: 'Grade 3',
    pattern: 'Morphology',
    examples: 'un-, re-, -ing, -ed'
  },
  '2-Syllable Words': {
    grade: 'Grade 2',
    pattern: 'Syllable Division',
    examples: 'rabbit, happen'
  },
  '3-Syllable Words': {
    grade: 'Grade 3',
    pattern: 'Multisyllabic Words',
    examples: 'banana, computer'
  },
  '4-Syllable Words': {
    grade: 'Grade 3+',
    pattern: 'Complex Multisyllabic',
    examples: 'helicopter, information'
  }
};

// Enhanced activity bank based on the lesson activity cheat sheet
const ACTIVITY_BANK = {
  'Letter Names': [
    'Alphabet arc practice with letter formation',
    'Sound wall review with letter names',
    'Letter sorting and matching games',
    'Name practice with magnetic letters',
    'Heart words (HFWs) with letter identification'
  ],
  'Letter Sounds': [
    'Phoneme drill with grapheme correspondence',
    'Sound boxes for phoneme isolation',
    'Alphabet arc with sound production',
    'Sound wall review and practice',
    'Blending board preparation activities'
  ],
  'VC & CVC': [
    'Word mapping with sound boxes',
    'Blending drill with CVC patterns',
    'Word sort by vowel sounds',
    'Blending board with manipulative letters',
    'Decodable text reading with CVC focus'
  ],
  'CVCC & CCVC': [
    'Word mapping with consonant blends',
    'Blending drill focusing on initial/final blends',
    'Word chains with blend manipulation',
    'Nonsense words with blend patterns',
    'Decodable text featuring blend words'
  ],
  'CVCe': [
    'Word mapping comparing CVC vs CVCe',
    'Word sort: short vowel vs long vowel patterns',
    'Blending drill with silent e words',
    'Word chains transforming CVC to CVCe',
    'Decodable text with CVCe patterns'
  ],
  'R-Controlled Vowels': [
    'Word mapping with r-controlled patterns',
    'Word sort by r-controlled vowel types (ar, er, ir, or, ur)',
    'Morpheme review with r-controlled base words',
    'Syllable types practice focusing on r-controlled',
    'Decodable text with r-controlled vocabulary'
  ],
  'Consonant Digraphs': [
    'Phoneme drill with digraph sounds',
    'Grapheme drill for digraph recognition',
    'Word mapping with digraph patterns',
    'Word sort by digraph types (sh, ch, th, wh)',
    'Decodable text featuring consonant digraphs'
  ],
  'Vowel Digraphs': [
    'Word mapping with vowel team patterns',
    'Word sort by vowel team sounds',
    'Morpheme review with vowel digraph base words',
    'Syllable types practice with vowel teams',
    'Timed reading with vowel team words'
  ],
  'Prefixes & Suffixes': [
    'Word work with morpheme analysis',
    'Morpheme review and identification',
    'Word chains adding/removing affixes',
    'Syllable types with affix patterns',
    'Sentence writing with morphologically complex words'
  ],
  '2-Syllable Words': [
    'Word work with syllable division',
    'Morpheme review for compound words',
    'Syllable types identification practice',
    'Word chains with two-syllable patterns',
    'Fluency practice with phrasing focus'
  ],
  '3-Syllable Words': [
    'Word work with advanced syllable division',
    'Morpheme review for complex words',
    'Syllable types in multisyllabic words',
    'Affixes practice with longer words',
    'Fluency with speed and phrasing goals'
  ],
  '4-Syllable Words': [
    'Word work with complex morphological analysis',
    'Morpheme review for academic vocabulary',
    'Syllable types in complex words',
    'Word chains with morpheme manipulation',
    'Fluency with speed, phrasing, and expression'
  ]
};

// Phase-specific activity suggestions based on developmental level
const getPhaseSpecificActivities = (skill, studentLevel = 'early') => {
  const baseActivities = ACTIVITY_BANK[skill] || [];
  
  // Add phase-specific phonemic awareness if appropriate
  const phonemicActivities = {
    'preK': ['Sound boxes', 'Sort sounds', 'Onset-rime activities', 'Syllable awareness', 'Rhyming', 'Beginning sounds'],
    'K-1st': ['Sound boxes', 'Sort sounds', 'Word chains', 'Phoneme blending', 'Phoneme segmentation', 'Phoneme ID'],
    '1st-3rd': ['Sound boxes', 'Sort sounds', 'Word chains', 'Phoneme blending', 'Phoneme segmentation', 'Phoneme manipulation'],
    '3rd+': ['Sound boxes', 'Blending', 'Segmenting', 'Word chains', 'Phoneme manipulation']
  };
  
  // Add connected text activities based on level
  const connectedTextActivities = {
    'preK': ['Decodable words', 'Decodable sentences', 'Decodable text'],
    'K-1st': ['Decodable text', 'Background knowledge', 'Vocabulary', 'Oral reading fluency', 'Retelling', 'Fiction & nonfiction'],
    '1st-3rd': ['Timed reading', 'Text discussion', 'Comprehension questions', 'Respond to text', 'Retelling', 'Text structure', 'Reader\'s theater', 'Fiction & nonfiction'],
    '3rd+': ['Timed reading', 'Text discussion', 'Comprehension questions', 'Respond to text', 'Retelling', 'Text structure', 'Reader\'s theater', 'Fiction & nonfiction']
  };
  
  return baseActivities;
};

export function analyzeStudentSkills(students, assessmentType) {
  console.log('🔍 Analyzing student skills for assessment type:', assessmentType);
  console.log('📚 Students to analyze:', students);
  
  return students.map(student => {
    console.log(`👤 Analyzing student: ${student.name}`);
    
    let lowestSkill = '';
    let assessmentDetail = '';
    let skillScore = 100;
    
    if (assessmentType === 'QPS' || assessmentType === 'CORE') {
      // Find lowest skill under 80% threshold
      const skills = [
        { name: 'Letter Names', score: student.letterNames },
        { name: 'Letter Sounds', score: student.letterSounds },
        { name: 'VC & CVC', score: student.vcCvc },
        { name: 'CVCC & CCVC', score: student.cvccCcvc },
        { name: 'CVCe', score: student.cvce },
        { name: 'R-Controlled Vowels', score: student.rControlled },
        { name: 'Consonant Digraphs', score: student.consonantDigraphs },
        { name: 'Vowel Digraphs', score: student.vowelDigraphs },
        { name: 'Prefixes & Suffixes', score: student.prefixesSuffixes },
        { name: '2-Syllable Words', score: student.twoSyllable },
        { name: '3-Syllable Words', score: student.threeSyllable },
        { name: '4-Syllable Words', score: student.fourSyllable }
      ];
      
      for (const skill of skills) {
        if (skill.score < 80) {
          lowestSkill = skill.name;
          skillScore = skill.score;
          assessmentDetail = `${assessmentType} ${skill.name}: ${skill.score}%`;
          break;
        }
      }
      
      if (!lowestSkill) {
        lowestSkill = '4-Syllable Words'; // If all above 80%, place in highest group
        assessmentDetail = `All ${assessmentType} skills above 80%`;
      }
    } else if (assessmentType === 'DIBELS') {
      // Map DIBELS scores to skills
      if (student.fsf < 30) {
        lowestSkill = 'Letter Sounds';
        assessmentDetail = `FSF: ${student.fsf}`;
      } else if (student.psf < 40) {
        lowestSkill = 'VC & CVC';
        assessmentDetail = `PSF: ${student.psf}`;
      } else if (student.nwfCls < 30) {
        lowestSkill = 'VC & CVC';
        assessmentDetail = `NWF-CLS: ${student.nwfCls}`;
      } else if (student.nwfWwr < 15) {
        lowestSkill = 'CVCC & CCVC';
        assessmentDetail = `NWF-WWR: ${student.nwfWwr}`;
      } else if (student.orfAccuracy < 95) {
        lowestSkill = 'Vowel Digraphs';
        assessmentDetail = `ORF Accuracy: ${student.orfAccuracy}%`;
      } else {
        lowestSkill = '2-Syllable Words';
        assessmentDetail = `ORF WCPM: ${student.orfWcpm}`;
      }
    } else if (assessmentType === 'THRIVE') {
      // Handle Thrive assessment - map to appropriate skills
      const skills = [
        { name: 'Letter Names', score: student.letterNames },
        { name: 'Letter Sounds', score: student.letterSounds },
        { name: 'VC & CVC', score: student.vcCvc },
        { name: 'CVCC & CCVC', score: student.cvccCcvc },
        { name: 'CVCe', score: student.cvce },
        { name: 'R-Controlled Vowels', score: student.rControlled },
        { name: 'Consonant Digraphs', score: student.consonantDigraphs },
        { name: 'Vowel Digraphs', score: student.vowelDigraphs },
        { name: 'Prefixes & Suffixes', score: student.prefixesSuffixes },
        { name: '2-Syllable Words', score: student.twoSyllable },
        { name: '3-Syllable Words', score: student.threeSyllable },
        { name: '4-Syllable Words', score: student.fourSyllable }
      ];
      
      for (const skill of skills) {
        if (skill.score < 80) {
          lowestSkill = skill.name;
          skillScore = skill.score;
          assessmentDetail = `${assessmentType} ${skill.name}: ${skill.score}%`;
          break;
        }
      }
      
      if (!lowestSkill) {
        lowestSkill = '4-Syllable Words'; // If all above 80%, place in highest group
        assessmentDetail = `All ${assessmentType} skills above 80%`;
      }
    }
    
    console.log(`🎯 Student ${student.name} lowest skill: ${lowestSkill}`);
    
    // Safety check: ensure the skill exists in our scope sequence
    const scopeInfo = SCOPE_SEQUENCE[lowestSkill];
    if (!scopeInfo) {
      console.error(`❌ Missing scope info for skill: ${lowestSkill}`);
      // Provide fallback
      const fallbackScopeInfo = {
        grade: 'Grade 1',
        pattern: 'Foundational Skills',
        examples: 'Basic reading skills'
      };
      const scopeReference = `${fallbackScopeInfo.grade} | ${fallbackScopeInfo.pattern} | ${fallbackScopeInfo.examples}`;
      
      return {
        ...student,
        lowestSkill,
        assessmentDetail,
        skillScore,
        scopeReference
      };
    }
    
    const scopeReference = `${scopeInfo.grade} | ${scopeInfo.pattern} | ${scopeInfo.examples}`;
    
    console.log(`✅ Student ${student.name} analysis complete:`, {
      lowestSkill,
      assessmentDetail,
      scopeReference
    });
    
    return {
      ...student,
      lowestSkill,
      assessmentDetail,
      skillScore,
      scopeReference
    };
  });
}

export function createGroups(analyzedStudents, numberOfGroups) {
  console.log('👥 Creating groups from analyzed students:', analyzedStudents);
  
  // Group students by their lowest skill
  const skillGroups = {};
  analyzedStudents.forEach(student => {
    const skill = student.lowestSkill;
    if (!skillGroups[skill]) {
      skillGroups[skill] = [];
    }
    skillGroups[skill].push(student);
  });
  
  console.log('📊 Skill groups:', skillGroups);
  
  // Convert to array and sort by skill progression
  const skillOrder = [
    'Letter Names',
    'Letter Sounds',
    'VC & CVC',
    'CVCC & CCVC',
    'CVCe',
    'Consonant Digraphs',
    'R-Controlled Vowels',
    'Vowel Digraphs',
    'Vowel Digraphs & Diphthongs',
    'Prefixes & Suffixes',
    '2-Syllable Words',
    '3-Syllable Words',
    '4-Syllable Words'
  ];
  
  let groups = [];
  skillOrder.forEach(skill => {
    if (skillGroups[skill]) {
      const scopeInfo = SCOPE_SEQUENCE[skill];
      
      // Safety check for scope info
      if (!scopeInfo) {
        console.warn(`⚠️ Missing scope info for skill: ${skill}, using fallback`);
        const fallbackScopeInfo = {
          grade: 'Grade 1',
          pattern: 'Foundational Skills',
          examples: 'Basic reading skills'
        };
        
        groups.push({
          skillFocus: skill,
          students: skillGroups[skill],
          scopeReference: `${fallbackScopeInfo.grade} | ${fallbackScopeInfo.pattern} | ${fallbackScopeInfo.examples}`,
          activities: ACTIVITY_BANK[skill] || [
            'Targeted skill practice',
            'Decodable text reading',
            'Assessment and progress monitoring'
          ]
        });
      } else {
        groups.push({
          skillFocus: skill,
          students: skillGroups[skill],
          scopeReference: `${scopeInfo.grade} | ${scopeInfo.pattern} | ${scopeInfo.examples}`,
          activities: ACTIVITY_BANK[skill] || [
            'Targeted skill practice',
            'Decodable text reading',
            'Assessment and progress monitoring'
          ]
        });
      }
    }
  });
  
  console.log('🎯 Initial groups created:', groups);
  
  // Handle group size constraints
  const finalGroups = [];
  groups.forEach(group => {
    if (group.students.length <= 6) {
      finalGroups.push(group);
    } else {
      // Split large groups
      const half = Math.ceil(group.students.length / 2);
      finalGroups.push({
        ...group,
        students: group.students.slice(0, half)
      });
      finalGroups.push({
        ...group,
        students: group.students.slice(half)
      });
    }
  });
  
  // Combine very small groups if needed
  const processedGroups = [];
  for (let i = 0; i < finalGroups.length; i++) {
    const currentGroup = finalGroups[i];
    if (currentGroup.students.length < 2 && i < finalGroups.length - 1) {
      const nextGroup = finalGroups[i + 1];
      processedGroups.push({
        skillFocus: `Mixed: ${currentGroup.skillFocus} & ${nextGroup.skillFocus}`,
        students: [...currentGroup.students, ...nextGroup.students],
        scopeReference: `${currentGroup.scopeReference} + ${nextGroup.scopeReference}`,
        activities: [
          ...currentGroup.activities.slice(0, 2),
          ...nextGroup.activities.slice(0, 1)
        ]
      });
      i++; // Skip next group since we combined it
    } else {
      processedGroups.push(currentGroup);
    }
  }
  
  const result = processedGroups.slice(0, numberOfGroups);
  console.log('✅ Final groups:', result);
  return result;
}